import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ReviewStep = ({ formData, onPrevious }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      navigate('/job-seeker-dashboard');
    }, 2000);
  };

  const anonymizedData = {
    id: "ANON-" + Math.random()?.toString(36)?.substr(2, 6)?.toUpperCase(),
    title: formData?.desiredPosition || "Anonymous Candidate",
    skills: formData?.skills || [],
    experience: formData?.experienceLevel || "Not specified",
    location: formData?.location || "Not specified",
    availability: formData?.availability || "Not specified",
    workArrangements: formData?.workArrangements || [],
    employmentTypes: formData?.employmentTypes || [],
    education: formData?.education || [],
    languages: formData?.languages || [],
    summary: formData?.professionalSummary || "No summary provided"
  };

  const getLocationLabel = (value) => {
    const locations = {
      'oslo': 'Oslo',
      'bergen': 'Bergen',
      'trondheim': 'Trondheim',
      'stavanger': 'Stavanger',
      'remote': 'Remote Only',
      'anywhere': 'Anywhere in Norway'
    };
    return locations?.[value] || value;
  };

  const getExperienceLabel = (value) => {
    const levels = {
      'entry': 'Entry Level (0-2 years)',
      'mid': 'Mid Level (3-5 years)',
      'senior': 'Senior Level (6-10 years)',
      'lead': 'Lead/Principal (10+ years)'
    };
    return levels?.[value] || value;
  };

  const getAvailabilityLabel = (value) => {
    const availability = {
      'immediately': 'Immediately',
      '2-weeks': 'Within 2 weeks',
      '1-month': 'Within 1 month',
      '2-months': 'Within 2 months',
      '3-months': 'Within 3 months',
      'flexible': 'Flexible'
    };
    return availability?.[value] || value;
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-2">
          Review Your Profile
        </h2>
        <p className="text-muted-foreground">
          This is how your anonymous profile will appear to employers
        </p>
      </div>
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Your Information */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Icon name="User" size={20} className="mr-2" />
            Your Information (Private)
          </h3>
          
          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="User" size={24} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">
                  {formData?.firstName} {formData?.lastName}
                </h4>
                <p className="text-sm text-muted-foreground">{formData?.email}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-foreground">Location:</span>
                <p className="text-muted-foreground">{getLocationLabel(formData?.location)}</p>
              </div>
              <div>
                <span className="font-medium text-foreground">Availability:</span>
                <p className="text-muted-foreground">{getAvailabilityLabel(formData?.availability)}</p>
              </div>
            </div>

            {formData?.portfolio && (
              <div className="text-sm">
                <span className="font-medium text-foreground">Portfolio:</span>
                <p className="text-muted-foreground break-all">{formData?.portfolio}</p>
              </div>
            )}
          </div>
        </div>

        {/* Anonymous Profile Preview */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Icon name="EyeOff" size={20} className="mr-2" />
            Anonymous Profile (Public)
          </h3>
          
          <div className="bg-muted/30 border border-border rounded-lg p-6">
            <div className="flex items-center space-x-4 mb-4">
              <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center">
                <Icon name="User" size={24} className="text-muted-foreground" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">{anonymizedData?.id}</h4>
                <p className="text-sm text-muted-foreground">{anonymizedData?.title}</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Skills */}
              {anonymizedData?.skills?.length > 0 && (
                <div>
                  <h5 className="font-medium text-foreground mb-2">Skills</h5>
                  <div className="flex flex-wrap gap-2">
                    {anonymizedData?.skills?.map((skill) => (
                      <span
                        key={skill?.id}
                        className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                      >
                        {skill?.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-foreground">Experience:</span>
                  <p className="text-muted-foreground">{getExperienceLabel(anonymizedData?.experience)}</p>
                </div>
                <div>
                  <span className="font-medium text-foreground">Location:</span>
                  <p className="text-muted-foreground">{getLocationLabel(anonymizedData?.location)}</p>
                </div>
              </div>

              {/* Work Preferences */}
              {anonymizedData?.workArrangements?.length > 0 && (
                <div>
                  <span className="font-medium text-foreground">Work Style:</span>
                  <p className="text-muted-foreground text-sm">
                    {anonymizedData?.workArrangements?.join(', ')}
                  </p>
                </div>
              )}

              {/* Languages */}
              {anonymizedData?.languages?.length > 0 && (
                <div>
                  <span className="font-medium text-foreground">Languages:</span>
                  <p className="text-muted-foreground text-sm">
                    {anonymizedData?.languages?.map(lang => 
                      `${lang?.language} (${lang?.proficiency})`
                    )?.join(', ')}
                  </p>
                </div>
              )}

              {/* Summary */}
              {anonymizedData?.summary && (
                <div>
                  <span className="font-medium text-foreground">Summary:</span>
                  <p className="text-muted-foreground text-sm mt-1">
                    {anonymizedData?.summary}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-4 p-3 bg-warning/10 rounded-lg border border-warning/20">
              <div className="flex items-start space-x-2">
                <Icon name="Lock" size={16} className="text-warning mt-0.5" />
                <div className="text-xs text-warning">
                  <div className="font-medium">Contact Hidden</div>
                  <div>Employers must pay 149 NOK to unlock your contact information</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Privacy Notice */}
      <div className="mt-8 bg-primary/5 rounded-lg p-6 border border-primary/20">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={24} className="text-primary mt-1" />
          <div>
            <h4 className="font-medium text-foreground mb-2">Your Privacy is Protected</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Your name, photo, and contact details are completely hidden</li>
              <li>• Employers can only see your skills, experience, and preferences</li>
              <li>• You control when to reveal your identity</li>
              <li>• All data is encrypted and GDPR compliant</li>
            </ul>
          </div>
        </div>
      </div>
      {/* Final Agreement */}
      <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="finalAgreement"
            className="mt-1 h-4 w-4 text-primary border-border rounded focus:ring-primary"
            defaultChecked
          />
          <label htmlFor="finalAgreement" className="text-sm text-muted-foreground">
            I confirm that all information provided is accurate and I understand how my 
            anonymous profile will be displayed to employers. I agree to receive notifications 
            when employers view or unlock my profile.
          </label>
        </div>
      </div>
      {/* Navigation */}
      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={onPrevious} iconName="ArrowLeft">
          Previous
        </Button>
        <Button
          onClick={handleSubmit}
          loading={isSubmitting}
          iconName={isSubmitting ? undefined : "Check"}
          iconPosition="right"
        >
          {isSubmitting ? 'Creating Profile...' : 'Create Profile'}
        </Button>
      </div>
    </div>
  );
};

export default ReviewStep;